// client/src/hooks/useFornecedores.js - VERSÃO COMPLETA
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react'; // ✅ IMPORTANTE: Adicionar esta linha
import api from '../services/api';
import useAlert from './useAlert';

const FORNECEDORES_QUERY_KEY = 'fornecedores';
const FORNECEDOR_DETAIL_QUERY_KEY = 'fornecedor';

// ✅ FUNÇÃO AUXILIAR: Sanitizar parâmetros
const sanitizeParams = (params) => {
  const sanitized = {};
  
  Object.keys(params).forEach(key => {
    const value = params[key];
    
    // Remover valores undefined, null, string vazia
    if (value !== undefined && value !== null && value !== '') {
      // Converter boolean para string
      if (typeof value === 'boolean') {
        sanitized[key] = value.toString();
      } else {
        sanitized[key] = value;
      }
    }
  });
  
  return sanitized;
};

// ✅ FUNÇÃO: Processar resposta da API
const processApiResponse = (responseData) => {
  if (!responseData) {
    return { success: false, data: [], count: 0, totalCount: 0, pagination: {} };
  }

  // Se já tem estrutura padrão
  if (responseData.success !== undefined && responseData.data !== undefined) {
    return {
      success: responseData.success,
      data: Array.isArray(responseData.data) ? responseData.data : [],
      count: responseData.count || 0,
      totalCount: responseData.totalCount || responseData.count || 0,
      pagination: responseData.pagination || {}
    };
  }

  // Se é um array direto
  if (Array.isArray(responseData)) {
    return {
      success: true,
      data: responseData,
      count: responseData.length,
      totalCount: responseData.length,
      pagination: {}
    };
  }

  // Outros casos
  return {
    success: true,
    data: responseData.data || responseData || [],
    count: Array.isArray(responseData.data) ? responseData.data.length : 0,
    totalCount: Array.isArray(responseData.data) ? responseData.data.length : 0,
    pagination: responseData.pagination || {}
  };
};

// ======================================================
// 🎯 HOOKS DE CONSULTA
// ======================================================

export const useFornecedores = (params = {}) => {
  return useQuery({
    queryKey: [FORNECEDORES_QUERY_KEY, params],
    queryFn: async () => {
      console.log('🔍 [USE_FORNECEDORES] Buscando fornecedores com params:', params);

      const cleanParams = sanitizeParams(params);

      try {
        const response = await api.get('/fornecedores', { 
          params: cleanParams,
          timeout: 15000
        });
        
        console.log('✅ [USE_FORNECEDORES] Resposta da API:', {
          success: response.data?.success,
          count: response.data?.count,
          totalCount: response.data?.totalCount,
          dataLength: response.data?.data?.length,
          primeiroFornecedor: response.data?.data?.[0]
        });
        
        return processApiResponse(response.data);
        
      } catch (error) {
        console.error('❌ [USE_FORNECEDORES] Erro:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {
            page: parseInt(cleanParams.page) || 1,
            limit: parseInt(cleanParams.limit) || 10,
            totalPages: 0,
            totalCount: 0,
            hasNext: false,
            hasPrev: false
          },
          error: error.message
        };
      }
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: 1,
  });
};

export const useFornecedor = (fornecedorId, options = {}) => {
  return useQuery({
    queryKey: [FORNECEDOR_DETAIL_QUERY_KEY, fornecedorId],
    queryFn: async () => {
      if (!fornecedorId) {
        return null;
      }
      
      console.log('🔍 [USE_FORNECEDOR] Buscando fornecedor:', fornecedorId);
      
      try {
        const response = await api.get(`/fornecedores/${fornecedorId}`, { 
          timeout: 10000 
        });
        
        return response.data?.data || response.data || null;
        
      } catch (error) {
        console.error('❌ [USE_FORNECEDOR] Erro:', error.message);
        return null;
      }
    },
    enabled: !!fornecedorId && (options.enabled !== undefined ? options.enabled : true),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });
};

export const useFornecedoresPorCategoria = (categoriaId, options = {}) => {
  return useQuery({
    queryKey: ['fornecedores-categoria', categoriaId, options],
    queryFn: async () => {
      if (!categoriaId) {
        throw new Error('Categoria ID é obrigatório');
      }
      
      console.log('🔍 [USE_FORNECEDORES_CATEGORIA] Buscando por categoria:', categoriaId);
      
      try {
        const response = await api.get(`/fornecedores/categoria/${categoriaId}`, { 
          params: options,
          timeout: 10000 
        });
        
        return processApiResponse(response.data);
          
      } catch (error) {
        console.error('❌ [USE_FORNECEDORES_CATEGORIA] Erro:', error.message);
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: 'Erro ao buscar fornecedores por categoria'
        };
      }
    },
    enabled: !!categoriaId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useFornecedoresPorTermo = (termo, options = {}) => {
  return useQuery({
    queryKey: ['fornecedores-busca', termo, options],
    queryFn: async () => {
      if (!termo || termo.length < 2) {
        return {
          success: true,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {}
        };
      }
      
      console.log('🔍 [USE_FORNECEDORES_TERMO] Buscando por termo:', termo);
      
      try {
        const response = await api.get(`/fornecedores/buscar/${termo}`, { 
          params: options,
          timeout: 10000 
        });
        
        return processApiResponse(response.data);
          
      } catch (error) {
        console.error('❌ [USE_FORNECEDORES_TERMO] Erro:', error.message);
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: 'Erro ao buscar fornecedores'
        };
      }
    },
    enabled: !!termo && termo.length >= 2,
    staleTime: 1000 * 60 * 5,
  });
};

export const useEstatisticasFornecedores = () => {
  return useQuery({
    queryKey: ['fornecedores-estatisticas'],
    queryFn: async () => {
      console.log('📊 [USE_ESTATISTICAS_FORNECEDORES] Buscando estatísticas');
      
      try {
        const response = await api.get('/fornecedores/estatisticas', { 
          timeout: 10000 
        });
        
        return response.data?.data || {};
          
      } catch (error) {
        console.error('❌ [USE_ESTATISTICAS_FORNECEDORES] Erro:', error.message);
        
        return {
          totalFornecedores: 0,
          fornecedoresAtivos: 0,
          fornecedoresInativos: 0,
          porTipoPessoa: {}
        };
      }
    },
    staleTime: 1000 * 60 * 10, // 10 minutos
  });
};

// ======================================================
// 🎯 HOOKS DE MUTAÇÃO (CRUD)
// ======================================================

export const useMutateFornecedores = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  const invalidateCaches = (fornecedorId = null) => {
    // Invalidar todas as queries relacionadas a fornecedores
    const queriesToInvalidate = [
      FORNECEDORES_QUERY_KEY,
      'fornecedores-categoria',
      'fornecedores-busca',
      'fornecedores-estatisticas'
    ];

    queriesToInvalidate.forEach(queryKey => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    });

    if (fornecedorId) {
      queryClient.invalidateQueries({ queryKey: [FORNECEDOR_DETAIL_QUERY_KEY, fornecedorId] });
    }

    console.log('🔄 [USE_MUTATE_FORNECEDORES] Caches invalidados');
  };

  const handleError = (error, defaultMessage) => {
    console.error('❌ [USE_MUTATE_FORNECEDORES] Erro:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    
    const errorMessage = error.response?.data?.message || 
                       error.response?.data?.error || 
                       error.message || 
                       defaultMessage;
    
    setAlert(errorMessage, 'danger');
    throw new Error(errorMessage);
  };

  const handleSuccess = (message, fornecedorId = null) => {
    invalidateCaches(fornecedorId);
    setAlert(message, 'success');
  };

  // ✅ MUTATION: Criar fornecedor
  const addFornecedor = useMutation({
    mutationFn: async (fornecedorData) => {
      console.log('🆕 [USE_MUTATE_FORNECEDORES] Criando fornecedor:', fornecedorData);
      const response = await api.post('/fornecedores', fornecedorData);
      return response.data;
    },
    onSuccess: (response) => {
      handleSuccess('Fornecedor criado com sucesso!', response.data?._id);
    },
    onError: (error) => handleError(error, 'Erro ao criar fornecedor'),
  });

  // ✅ MUTATION: Atualizar fornecedor
  const updateFornecedor = useMutation({
    mutationFn: async ({ id, fornecedorData }) => {
      console.log('✏️ [USE_MUTATE_FORNECEDORES] Atualizando fornecedor:', { id, fornecedorData });
      const response = await api.put(`/fornecedores/${id}`, fornecedorData);
      return response.data;
    },
    onSuccess: (response, variables) => {
      handleSuccess('Fornecedor atualizado com sucesso!', variables.id);
    },
    onError: (error) => handleError(error, 'Erro ao atualizar fornecedor'),
  });

  // ✅ MUTATION: Deletar fornecedor
  const deleteFornecedor = useMutation({
    mutationFn: async (fornecedorId) => {
      console.log('🗑️ [USE_MUTATE_FORNECEDORES] Removendo fornecedor:', fornecedorId);
      const response = await api.delete(`/fornecedores/${fornecedorId}`);
      return response.data;
    },
    onSuccess: (response, fornecedorId) => {
      handleSuccess('Fornecedor removido com sucesso!', fornecedorId);
    },
    onError: (error) => handleError(error, 'Erro ao remover fornecedor'),
  });

  // ✅ MUTATION: Upload de foto
  const uploadFotoFornecedor = useMutation({
    mutationFn: async ({ fornecedorId, foto }) => {
      console.log('🖼️ [USE_MUTATE_FORNECEDORES] Upload foto para fornecedor:', fornecedorId);
      
      const formData = new FormData();
      formData.append('foto', foto);
      
      const response = await api.put(`/fornecedores/${fornecedorId}/foto`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000,
      });
      
      return response.data;
    },
    onSuccess: (response, variables) => {
      handleSuccess('Foto do fornecedor atualizada com sucesso!', variables.fornecedorId);
    },
    onError: (error) => handleError(error, 'Erro ao fazer upload da foto'),
  });

  // ✅ MUTATION: Remover foto
  const removeFotoFornecedor = useMutation({
    mutationFn: async (fornecedorId) => {
      console.log('🖼️ [USE_MUTATE_FORNECEDORES] Removendo foto do fornecedor:', fornecedorId);
      const response = await api.delete(`/fornecedores/${fornecedorId}/foto`);
      return response.data;
    },
    onSuccess: (response, fornecedorId) => {
      handleSuccess('Foto do fornecedor removida com sucesso!', fornecedorId);
    },
    onError: (error) => handleError(error, 'Erro ao remover foto'),
  });

  // ✅ MUTATION: Alternar status
  const toggleStatusFornecedor = useMutation({
    mutationFn: async (fornecedorId) => {
      console.log('🔄 [USE_MUTATE_FORNECEDORES] Alternando status do fornecedor:', fornecedorId);
      const response = await api.patch(`/fornecedores/${fornecedorId}/alternar-status`);
      return response.data;
    },
    onSuccess: (response, fornecedorId) => {
      handleSuccess('Status do fornecedor alternado com sucesso!', fornecedorId);
    },
    onError: (error) => handleError(error, 'Erro ao alternar status do fornecedor'),
  });

  // ✅ MUTATION: Adicionar contato
  const adicionarContatoFornecedor = useMutation({
    mutationFn: async ({ fornecedorId, contatoData }) => {
      console.log('👤 [USE_MUTATE_FORNECEDORES] Adicionando contato:', { fornecedorId, contatoData });
      const response = await api.post(`/fornecedores/${fornecedorId}/contatos`, contatoData);
      return response.data;
    },
    onSuccess: (response, variables) => {
      handleSuccess('Contato adicionado com sucesso!', variables.fornecedorId);
    },
    onError: (error) => handleError(error, 'Erro ao adicionar contato'),
  });

  // ✅ MUTATION: Remover contato
  const removerContatoFornecedor = useMutation({
    mutationFn: async ({ fornecedorId, contatoId }) => {
      console.log('👤 [USE_MUTATE_FORNECEDORES] Removendo contato:', { fornecedorId, contatoId });
      const response = await api.delete(`/fornecedores/${fornecedorId}/contatos/${contatoId}`);
      return response.data;
    },
    onSuccess: (response, variables) => {
      handleSuccess('Contato removido com sucesso!', variables.fornecedorId);
    },
    onError: (error) => handleError(error, 'Erro ao remover contato'),
  });

  const isLoading = {
    add: addFornecedor.isPending,
    update: updateFornecedor.isPending,
    delete: deleteFornecedor.isPending,
    uploadFoto: uploadFotoFornecedor.isPending,
    removeFoto: removeFotoFornecedor.isPending,
    toggleStatus: toggleStatusFornecedor.isPending,
    addContato: adicionarContatoFornecedor.isPending,
    removeContato: removerContatoFornecedor.isPending
  };

  const isPending = Object.values(isLoading).some(status => status);

  return {
    // ✅ MUTATIONS PRINCIPAIS
    addFornecedor,
    updateFornecedor,
    deleteFornecedor,
    
    // ✅ MUTATIONS DE FOTO
    uploadFotoFornecedor,
    removeFotoFornecedor,
    
    // ✅ MUTATIONS DE STATUS
    toggleStatusFornecedor,
    
    // ✅ MUTATIONS DE CONTATOS
    adicionarContatoFornecedor,
    removerContatoFornecedor,
    
    // ✅ ESTADOS DE LOADING
    isPending,
    isLoading,
    
    // ✅ UTILITÁRIOS DE CACHE
    invalidateCaches,
    forceRefetch: () => invalidateCaches(),
  };
};

// ======================================================
// 🎯 HOOK DE GERENCIAMENTO COMPOSTO
// ======================================================

export const useFornecedorManagement = (params = {}) => {
  const fornecedores = useFornecedores(params);
  const estatisticas = useEstatisticasFornecedores();
  const mutations = useMutateFornecedores();

  return {
    // ✅ DADOS
    fornecedores: fornecedores.data,
    estatisticas: estatisticas.data,
    
    // ✅ ESTADOS
    isLoading: fornecedores.isLoading || estatisticas.isLoading,
    isError: fornecedores.isError || estatisticas.isError,
    error: fornecedores.error || estatisticas.error,
    
    // ✅ MUTATIONS
    ...mutations,
    
    // ✅ AÇÕES
    refetch: () => {
      fornecedores.refetch();
      estatisticas.refetch();
    },
    
    refetchAll: () => {
      fornecedores.refetch();
      estatisticas.refetch();
      mutations.forceRefetch();
    }
  };
};

// ======================================================
// 🎯 HOOKS ESPECIALIZADOS
// ======================================================

export const useFornecedorForm = (fornecedorId = null) => {
  const { data: fornecedor, isLoading, error } = useFornecedor(fornecedorId);
  const { updateFornecedor, addFornecedor, isPending } = useMutateFornecedores();

  const salvarFornecedor = async (fornecedorData) => {
    if (fornecedorId) {
      return await updateFornecedor.mutateAsync({ 
        id: fornecedorId, 
        fornecedorData 
      });
    } else {
      return await addFornecedor.mutateAsync(fornecedorData);
    }
  };

  return {
    fornecedor,
    isLoading: isLoading || isPending,
    error,
    salvarFornecedor,
    isEditando: !!fornecedorId
  };
};

export const useFornecedorFoto = (fornecedorId) => {
  const { data: fornecedor } = useFornecedor(fornecedorId);
  const { uploadFotoFornecedor, removeFotoFornecedor } = useMutateFornecedores();

  return {
    foto: fornecedor?.foto,
    uploadFoto: (foto) => uploadFotoFornecedor.mutateAsync({ fornecedorId, foto }),
    removeFoto: () => removeFotoFornecedor.mutateAsync(fornecedorId),
    isLoading: uploadFotoFornecedor.isPending || removeFotoFornecedor.isPending
  };
};

export const useFornecedorContatos = (fornecedorId) => {
  const { data: fornecedor } = useFornecedor(fornecedorId);
  const { adicionarContatoFornecedor, removerContatoFornecedor } = useMutateFornecedores();

  return {
    contatos: fornecedor?.contatos || [],
    adicionarContato: (contatoData) => 
      adicionarContatoFornecedor.mutateAsync({ fornecedorId, contatoData }),
    removerContato: (contatoId) => 
      removerContatoFornecedor.mutateAsync({ fornecedorId, contatoId }),
    isLoading: adicionarContatoFornecedor.isPending || removerContatoFornecedor.isPending
  };
};

// ======================================================
// 🎯 HOOKS PARA FORMULÁRIOS E SELECTS
// ======================================================

/**
 * @desc Hook para obter fornecedores formatados para selects em formulários
 * @param {Object} options - Opções de filtro
 * @returns {Object} Dados formatados para selects
 */
export const useFornecedoresParaFormulario = (options = {}) => {
  const { 
    fornecedores, 
    isLoading, 
    error 
  } = useFornecedores({
    ...options,
    limit: 100, // Limite maior para selects
    ativo: 'true' // Por padrão, apenas ativos
  });

  // Formatar dados para selects
  const fornecedoresOptions = React.useMemo(() => {
    if (!fornecedores?.data) return [];
    
    return fornecedores.data.map(fornecedor => ({
      value: fornecedor._id,
      label: fornecedor.nomeFantasia || fornecedor.razaoSocial,
      document: fornecedor.documentoPrincipal,
      tipoPessoa: fornecedor.tipoPessoa,
      ...fornecedor
    }));
  }, [fornecedores]);

  return {
    fornecedores: fornecedoresOptions,
    isLoading,
    error,
    rawData: fornecedores?.data || []
  };
};

/**
 * @desc Hook para busca rápida de fornecedores (autocomplete)
 * @param {string} searchTerm - Termo de busca
 * @param {Object} options - Opções adicionais
 * @returns {Object} Dados formatados para autocomplete
 */
export const useFornecedoresAutocomplete = (searchTerm = '', options = {}) => {
  const { 
    fornecedores, 
    isLoading 
  } = useFornecedoresPorTermo(searchTerm, {
    limit: 20,
    ...options
  });

  const fornecedoresOptions = React.useMemo(() => {
    if (!fornecedores?.data) return [];
    
    return fornecedores.data.map(fornecedor => ({
      value: fornecedor._id,
      label: `${fornecedor.nomeFantasia || fornecedor.razaoSocial} - ${fornecedor.documentoPrincipal}`,
      data: fornecedor
    }));
  }, [fornecedores]);

  return {
    options: fornecedoresOptions,
    isLoading,
    data: fornecedores?.data || []
  };
};

/**
 * @desc Hook para select simples de fornecedores
 * @param {boolean} includeAll - Incluir opção "Todos"
 * @returns {Object} Opções formatadas para select
 */
export const useFornecedoresSelect = (includeAll = false) => {
  const { fornecedores, isLoading } = useFornecedoresParaFormulario();

  const options = React.useMemo(() => {
    const baseOptions = fornecedores || [];
    
    if (includeAll) {
      return [
        { value: '', label: 'Todos os Fornecedores' },
        ...baseOptions
      ];
    }
    
    return baseOptions;
  }, [fornecedores, includeAll]);

  return {
    options,
    isLoading,
    fornecedores: fornecedores || []
  };
};

export default useFornecedores;